import { defineStore } from 'pinia';
import { amountHexagonsKey, useFilterStore } from './FilterStore';
import { computed, ComputedRef, ref, Ref, watch } from 'vue';
import _ from 'lodash';
import { useConfiguratorStore } from './ConfiguratorStore';
import { useMediaclipCommunication } from '@/composables/UseMediaclipCommunication';

export const useMediaclipStore = defineStore(
  `mediaclip-${window.product.sku}`,
  () => {
    const mediaclipCommunication = useMediaclipCommunication();

    const isAutofillCancelled: Ref<boolean> = ref(false);
    const currentWorksheet: Ref<number> = ref(1);
    const filledWorksheets: Ref<number[]> = ref([]);
    const amountOfWorksheets: ComputedRef<number> = computed(() => {
      const value = Number(
        useFilterStore().selectedFilterValues[amountHexagonsKey]?.value
      );
      if (isNaN(value)) {
        return 0;
      }
      return value;
    });
    const emptyWorksheets: Ref<number[]> = ref([]);

    function $reset() {
      isAutofillCancelled.value = false;
      currentWorksheet.value = 1;
      filledWorksheets.value = [];
    }

    function watchPreviousButtonClick(): void {
      if (currentWorksheet.value === 1) {
        currentWorksheet.value = amountOfWorksheets.value;
        return;
      }

      currentWorksheet.value--;
    }

    function watchNextButtonClick(): void {
      if (currentWorksheet.value === amountOfWorksheets.value) {
        currentWorksheet.value = 1;
        return;
      }

      currentWorksheet.value++;
    }

    function addFilledWorksheet(): void {
      if (!_.includes(filledWorksheets.value, currentWorksheet.value)) {
        filledWorksheets.value.push(currentWorksheet.value);
        mediaclipCommunication.emitEvent('checkAllProjectsForPhotos');
      }
    }

    function removePhoto(): void {
      emptyWorksheets.value.push(currentWorksheet.value);
      _.remove(
        filledWorksheets.value,
        worksheet => worksheet === currentWorksheet.value
      );

      mediaclipCommunication.saveProject();
    }

    watch(
      () => amountOfWorksheets.value,
      newValue => {
        // On amount of worksheets change, change the empty worksheets array
        emptyWorksheets.value = useConfiguratorStore().isEditing
          ? []
          : _.range(1, newValue + 1);
      }
    );
    return {
      isAutofillCancelled,
      emptyWorksheets,
      filledWorksheets,
      currentWorksheet,
      amountOfWorksheets,
      watchPreviousButtonClick,
      watchNextButtonClick,
      addFilledWorksheet,
      removePhoto,
      $reset,
    };
  },
  {
    persist: {
      paths: [
        'isAutofillCancelled',
        'isDisabledForWorksheetContainer',
        'emptyWorksheets',
        'filledWorksheets',
      ],
    },
  }
);
